import { fetchApi } from '@/lib/api';
import { useAuthStore } from '@/stores/authStore';
import { useTeamStore } from '@/stores/team';

/**
 * Team members' names and photos. A name set here stops sign-in overwriting it from Google;
 * an uploaded photo wins over the one from their sign-in account.
 */

export const MEMBER_PHOTO_TYPES = 'image/jpeg,image/png,image/webp,image/gif';
const MAX_BYTES = 5 * 1024 * 1024;
export const DISPLAY_NAME_MAX = 80;

export type MemberProfile = {
  displayname: string | null;
  email: string;
  photourl: string | null;
  has_uploaded_photo: boolean;
};

export function getMemberProfile(userId: string) {
  return fetchApi<{ profile: MemberProfile }>(`/team/members/${userId}/profile`);
}

export async function setMemberName(userId: string, displayname: string) {
  const res = await fetchApi<{ displayname: string }>(`/team/members/${userId}/name`, {
    method: 'PUT',
    body: JSON.stringify({ displayname }),
  });
  if (res.data) showMemberChange(userId, { displayname: res.data.displayname });
  return res;
}

/** `photo` is the cropped square from PhotoCropDialog. */
export async function uploadMemberPhoto(userId: string, photo: Blob) {
  if (photo.size > MAX_BYTES) return { data: null, error: 'Photos can be up to 5 MB' };
  const form = new FormData();
  form.append('file', photo, 'photo.jpg');
  const res = await fetchApi<{ photourl: string }>(`/team/members/${userId}/photo`, {
    method: 'PUT',
    body: form,
  });
  if (res.data) showMemberChange(userId, { photourl: res.data.photourl });
  return res;
}

export async function removeMemberPhoto(userId: string) {
  const res = await fetchApi<{ photourl: string | null }>(`/team/members/${userId}/photo`, {
    method: 'DELETE',
  });
  if (res.data) showMemberChange(userId, { photourl: res.data.photourl });
  return res;
}

type MemberChange = { displayname?: string; photourl?: string | null };

/** Show a change straight away on the Team page and, for yourself, in the top bar. */
function showMemberChange(userId: string, change: MemberChange) {
  const { members, setMembers } = useTeamStore.getState();
  if (members.some((m) => m.user_id === userId)) {
    setMembers(members.map((m) => (m.user_id === userId ? { ...m, ...change } : m)));
  }
  const { user, setUser } = useAuthStore.getState();
  if (user && user.id === userId) {
    setUser({
      ...user,
      ...(change.displayname !== undefined ? { displayName: change.displayname } : {}),
      ...(change.photourl !== undefined ? { photoURL: change.photourl ?? '' } : {}),
    });
  }
}
